import { X, UserCircle2, Shield } from 'lucide-react';

interface EditUserModalProps {
  user: { id: number; name: string; email: string; role: string; status: string } | null;
  isOpen: boolean;
  onClose: () => void;
  selectedRole: string;
  setSelectedRole: (role: string) => void;
  userStatus: string;
  setUserStatus: (status: string) => void;
  roles: string[];
  onSave: () => void;
}

export function EditUserModal({
  user,
  isOpen,
  onClose,
  selectedRole,
  setSelectedRole,
  userStatus,
  setUserStatus,
  roles,
  onSave,
}: EditUserModalProps) {
  if (!isOpen || !user) return null;

  const handleSave = () => {
    // Persistence is handled by the parent (AdminSettings.handleSaveUser) —
    // it owns the form state (selectedRole, userStatus) and the API call.
    // This modal is a presentational wrapper; just forward the click.
    onSave();
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#486581] to-[#102a43] p-6 text-white flex items-center justify-between rounded-t-xl">
            <div className="flex items-center gap-3">
              <UserCircle2 className="w-8 h-8" />
              <div>
                <h2 className="text-xl font-medium">Edit User</h2>
                <p className="text-sm text-[#bcccdc]">Assign role and manage user access</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* User Info */}
            <div className="bg-[#f0f4f8] p-5 rounded-xl border-2 border-[#bcccdc]">
              <h3 className="font-medium text-[#102a43] mb-3 flex items-center gap-2">
                <UserCircle2 className="w-5 h-5 text-[#486581]" />
                User Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-[#829ab1] mb-1">Name</div>
                  <div className="font-medium text-[#102a43]">{user.name}</div>
                </div>
                <div>
                  <div className="text-sm text-[#829ab1] mb-1">Email</div>
                  <div className="font-medium text-[#102a43]">{user.email}</div>
                </div>
              </div>
            </div>

            {/* Role Assignment */}
            <div className="bg-white p-5 rounded-xl border-2 border-[#bcccdc]">
              <h3 className="font-medium text-[#102a43] mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#486581]" />
                Role Assignment
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-[#486581] mb-2">
                    Select Role
                  </label>
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-[#bcccdc] rounded-lg focus:outline-none focus:border-[#486581] bg-white"
                  >
                    {roles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Role Description */}
                <div className="bg-[#f0f4f8] p-4 rounded-lg border border-[#bcccdc]">
                  <div className="text-sm text-[#486581]">
                    <strong className="text-[#102a43]">Selected Role:</strong> {selectedRole}
                  </div>
                  <div className="text-sm text-[#829ab1] mt-2">
                    {selectedRole === 'Executive Sponsor' && 'Full access to all features with strategic oversight and approval authority.'}
                    {selectedRole === 'Portfolio Manager' && 'Manage portfolios, programs, and strategic planning.'}
                    {selectedRole === 'Program Manager' && 'Manage programs, projects, and dependencies.'}
                    {selectedRole === 'Project Manager' && 'Manage individual projects, tasks, and deliverables.'}
                    {selectedRole === 'PMO Lead' && 'Administrative access to governance, compliance, and standards.'}
                    {selectedRole === 'Team Lead' && 'Manage team resources and task assignments.'}
                    {selectedRole === 'Team Member' && 'Update tasks and contribute to projects.'}
                    {selectedRole === 'Stakeholder (View Only)' && 'View-only access to reports and dashboards.'}
                  </div>
                </div>
              </div>
            </div>

            {/* User Status */}
            <div className="bg-white p-5 rounded-xl border-2 border-[#bcccdc]">
              <h3 className="font-medium text-[#102a43] mb-3">User Status</h3>
              <div className="space-y-2">
                {['Active', 'Inactive', 'Suspended'].map((status) => (
                  <label
                    key={status}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      userStatus === status
                        ? 'border-[#486581] bg-[#486581]/5'
                        : 'border-[#bcccdc] hover:border-[#829ab1]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={status}
                      checked={userStatus === status}
                      onChange={(e) => setUserStatus(e.target.value)}
                      className="w-4 h-4 text-[#486581]"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-[#102a43]">{status}</div>
                      <div className="text-xs text-[#829ab1]">
                        {status === 'Active' && 'User can access the platform'}
                        {status === 'Inactive' && 'User cannot log in but data is retained'}
                        {status === 'Suspended' && 'Temporarily blocked access pending review'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Permission Preview */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border-2 border-blue-200">
              <h3 className="font-medium text-[#102a43] mb-2">Permission Preview</h3>
              <p className="text-sm text-[#486581] mb-3">
                With the <strong>{selectedRole}</strong> role, this user will have access to:
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {selectedRole === 'Executive Sponsor' && (
                  <>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Portfolio Management</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Strategic Planning</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Budget Approvals</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Executive Dashboards</div>
                  </>
                )}
                {selectedRole === 'Program Manager' && (
                  <>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Program Management</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Project Oversight</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Resource Planning</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Risk Management</div>
                  </>
                )}
                {selectedRole === 'Project Manager' && (
                  <>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Project Management</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Task Management</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Team Collaboration</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Progress Tracking</div>
                  </>
                )}
                {selectedRole === 'PMO Lead' && (
                  <>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Governance</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Compliance</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Standards</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> User Management</div>
                  </>
                )}
                {(selectedRole === 'Team Lead' || selectedRole === 'Team Member') && (
                  <>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Task Updates</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Time Tracking</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Team Collaboration</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> Document Access</div>
                  </>
                )}
                {selectedRole === 'Stakeholder (View Only)' && (
                  <>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> View Dashboards</div>
                    <div className="flex items-center gap-2"><span className="text-green-600">✓</span> View Reports</div>
                    <div className="flex items-center gap-2"><span className="text-red-600">✗</span> No Edit Access</div>
                    <div className="flex items-center gap-2"><span className="text-red-600">✗</span> No Admin Access</div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-[#f0f4f8] px-6 py-4 flex items-center justify-between border-t-2 border-[#bcccdc] rounded-b-xl">
            <button
              onClick={onClose}
              className="px-6 py-2.5 border-2 border-[#bcccdc] text-[#486581] rounded-lg hover:bg-white transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2.5 bg-gradient-to-r from-[#486581] to-[#102a43] text-white rounded-lg hover:from-[#102a43] hover:to-[#486581] transition-all shadow-lg hover:shadow-xl font-medium"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

